import { Trans } from '@lingui/react/macro';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PendingIcon from '@mui/icons-material/Pending';
import { Accordion, AccordionDetails, AccordionSummary, Button, Card, CardContent, Link, Stack, Typography, useTheme } from '@mui/material';
import { format, isValid } from 'date-fns';
import { formatCurrency } from '../utils/formatCurrency';

export interface CompletedTasksListProps {
  applicationCompletedDate?: string;
  depositPaidStatus: 'Yes' | 'No' | 'Pending';
  depositPaidDate?: string;
  programPrice?: number;
  programCurrency?: string;
  showApplicationTask?: boolean;
  showPaymentTask?: boolean;
  paymentProvider?: 'verifone' | 'nexpora';
}

export const CompletedTasksList = ({
  applicationCompletedDate,
  depositPaidStatus,
  depositPaidDate,
  programPrice,
  programCurrency = 'USD',
  showApplicationTask = false,
  showPaymentTask = false,
  paymentProvider,
}: CompletedTasksListProps) => {
  const theme = useTheme();

  // Ideally we get the formatted date from the backend
  const formatDisplayDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return isValid(date) ? format(date, 'dd/MM/yyyy') : dateString;
  };

  const formatPrice = (price?: number) => formatCurrency(price, programCurrency);

  const getPaymentStatusIcon = () => {
    switch (depositPaidStatus) {
      case 'Yes':
        return <CheckCircleOutlineIcon color="success" />;
      case 'Pending':
        return <PendingIcon color="warning" />;
      default:
        return <PendingIcon color="disabled" />;
    }
  };

  const getPaymentStatusText = () => {
    switch (depositPaidStatus) {
      case 'Yes':
        return depositPaidDate ? <Trans>Paid on {formatDisplayDate(depositPaidDate)}</Trans> : <Trans>Payment Completed</Trans>;
      case 'Pending':
        return <Trans>Payment Pending</Trans>;
      default:
        return <Trans>Payment Required</Trans>;
    }
  };

  // Don't render if no tasks to show
  if (!showApplicationTask && !showPaymentTask) {
    return null;
  }

  return (
    <Card>
      <CardContent>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content" id="panel1-header" sx={{ pb: 1 }}>
            <Stack direction="row" alignItems="center" gap={1}>
              <ChecklistIcon />
              <Typography>
                <Trans>Completed Tasks</Trans>
              </Typography>
            </Stack>
          </AccordionSummary>

          {/* Application Task */}
          {showApplicationTask && applicationCompletedDate && (
            <AccordionDetails>
              <Stack direction="row" justifyContent="space-between" gap={1}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <CheckCircleOutlineIcon color="success" />
                  <Stack>
                    <Typography variant="h3" fontSize="18px">
                      <Trans>Course Reservation Application</Trans>
                    </Typography>
                    <Typography variant="caption" color={theme.palette.secondary.main}>
                      <Trans>Completed on {formatDisplayDate(applicationCompletedDate)}</Trans>
                    </Typography>
                  </Stack>
                </Stack>
                <Button component={Link} href="/course-reservation" variant="outlined" size="small" sx={{ '&:hover': { color: '#00968F' } }}>
                  <Typography fontSize="inherit" lineHeight="inherit" component="span">
                    <Trans>View Details</Trans>
                  </Typography>
                </Button>
              </Stack>
            </AccordionDetails>
          )}

          {/* Payment Task */}
          {showPaymentTask && (
            <AccordionDetails>
              <Stack direction="row" justifyContent="space-between" gap={1}>
                <Stack direction="row" alignItems="center" gap={1}>
                  {getPaymentStatusIcon()}
                  <Stack>
                    <Typography variant="h3" fontSize="18px">
                      <Trans>Initial Deposit</Trans>
                      {programPrice && (
                        <Typography component="span" variant="body2" color={theme.palette.text.secondary} sx={{ ml: 1 }}>
                          ({formatPrice(programPrice)})
                        </Typography>
                      )}
                      {paymentProvider && (
                        <Typography component="span" variant="caption" color={theme.palette.text.secondary} sx={{ ml: 1 }}>
                          via {paymentProvider}
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="caption" color={theme.palette.secondary.main}>
                      {getPaymentStatusText()}
                    </Typography>
                  </Stack>
                </Stack>
                {depositPaidStatus === 'Yes' && (
                  <Button variant="outlined" size="small">
                    <Typography fontSize="inherit" lineHeight="inherit" component="span">
                      <Trans>View Receipt</Trans>
                    </Typography>
                  </Button>
                )}
              </Stack>
            </AccordionDetails>
          )}
        </Accordion>
      </CardContent>
    </Card>
  );
};
